import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IOwnerStore } from '@services/auth.service';

@Component({
  selector: 'app-store-picker-dialog',
  standalone: false,
  templateUrl: './store-picker-dialog.component.html',
  styleUrl: './store-picker-dialog.component.scss',
})
export class StorePickerDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() stores: IOwnerStore[] = [];
  @Input() loading = false;
  @Input() closable = true;
  /** Currently-active store; that card will be marked "current" and click disabled. */
  @Input() currentStoreId: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() storeSelected = new EventEmitter<string>();

  currentLang: string = 'ar';
  uniqueStores: IOwnerStore[] = [];
  private destroy$ = new Subject<void>();

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang || this.translate.getDefaultLang() || 'ar';
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.currentLang = event.lang;
      });
    this.recomputeUniqueStores();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stores']) {
      this.recomputeUniqueStores();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onSelect(store: IOwnerStore): void {
    if (this.loading) return;
    if (!store?._id) return;
    if (store._id === this.currentStoreId) return;
    this.storeSelected.emit(store._id);
  }

  storeName(store: IOwnerStore): string {
    if (!store?.name) return '';
    return this.currentLang === 'en' ? store.name.en : store.name.ar;
  }

  isCurrent(store: IOwnerStore): boolean {
    return !!this.currentStoreId && store?._id === this.currentStoreId;
  }

  trackById(_: number, store: IOwnerStore): string {
    return store?._id ?? String(_);
  }

  /**
   * De-duplicate by `_id` first, then by a display-fingerprint (name + logo
   * + active state). This handles both literal duplicates and the case where
   * the backend returns distinct ObjectIds that represent the same underlying
   * store (e.g. legacy ownership rows joined twice).
   */
  private recomputeUniqueStores(): void {
    const list = Array.isArray(this.stores) ? this.stores : [];
    const seenIds = new Set<string>();
    const seenFingerprints = new Set<string>();
    const deduped: IOwnerStore[] = [];
    let droppedById = 0;
    let droppedByFingerprint = 0;

    for (const store of list) {
      if (!store || !store._id) continue;
      if (seenIds.has(store._id)) {
        droppedById++;
        continue;
      }
      const fingerprint = JSON.stringify({
        n: store.name ?? null,
        l: store.logo?.url ?? null,
        a: !!store.isActive,
      });
      if (seenFingerprints.has(fingerprint)) {
        droppedByFingerprint++;
        continue;
      }
      seenIds.add(store._id);
      seenFingerprints.add(fingerprint);
      deduped.push(store);
    }

    if (droppedById || droppedByFingerprint) {
      // eslint-disable-next-line no-console
      console.warn(
        `[StorePickerDialog] Dropped ${droppedById} duplicate _id(s) and ${droppedByFingerprint} visually-identical store(s). Raw count: ${list.length}, rendered: ${deduped.length}.`
      );
    }

    this.uniqueStores = deduped;
  }
}
